import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import { getGoldRates } from "@vaulzo/core/api/market";

const KARATS = [
  { value: "24", label: "24K", rateKey: "rate_24k" },
  { value: "22", label: "22K", rateKey: "rate_22k" },
  { value: "18", label: "18K", rateKey: "rate_18k" },
];

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for a gold portfolio: one row per item (label + karat +
 * grams + optional buy rate), valued live against the official IBJA
 * domestic rates. The server does the authoritative pricing on save.
 */
export default function GoldHoldingsEditor({ holdings, setHoldings }) {
  const { data: rates } = useQuery({
    queryKey: ["goldRates"],
    queryFn: getGoldRates,
    staleTime: 60 * 60 * 1000, // IBJA publishes twice a business day
  });

  const rateFor = (karat) => rates?.[KARATS.find((k) => k.value === karat)?.rateKey] || null;

  const patchRow = (i, patch) =>
    setHoldings((rows) => rows.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings((rows) => rows.filter((_, idx) => idx !== i));
  const addRow = () =>
    setHoldings((rows) => [...rows, { label: "", karat: "22", grams: "", buyPerGram: "" }]);

  let total = 0;
  let invested = 0;
  let investedNow = 0;
  holdings.forEach((h) => {
    const grams = parseFloat(h.grams);
    const rate = rateFor(h.karat);
    if (!(grams > 0) || !rate) return;
    total += grams * rate;
    const buy = parseFloat(h.buyPerGram);
    if (buy > 0) {
      invested += grams * buy;
      investedNow += grams * rate;
    }
  });
  const gain = invested > 0 ? ((investedNow - invested) / invested) * 100 : null;

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Gold items</span>
        <span className="text-[12px] text-vaulzo-fainter">
          {rates ? `IBJA: 22K ₹${Math.round(rates.rate_22k).toLocaleString("en-IN")}/g` : "Buy rate is optional"}
        </span>
      </div>

      {holdings.map((h, i) => {
        const grams = parseFloat(h.grams);
        const rate = rateFor(h.karat);
        const rowValue = grams > 0 && rate ? grams * rate : null;
        const buy = parseFloat(h.buyPerGram);
        const rowGain = rowValue && buy > 0 ? ((rate - buy) / buy) * 100 : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-3 first:pt-1 last:border-b-0">
            <div className="flex items-center gap-3">
              <input
                value={h.label}
                onChange={(e) => patchRow(i, { label: e.target.value })}
                placeholder={`e.g. Chain, coins… (optional)`}
                className={`min-w-0 flex-1 text-[14px] ${underline}`}
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove item"
                className="flex w-5 flex-none justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            <div className="mt-2 flex gap-4">
              <label className="w-[90px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Karat
                </span>
                <select
                  value={h.karat}
                  onChange={(e) => patchRow(i, { karat: e.target.value })}
                  className={`w-full cursor-pointer text-[15px] font-semibold ${underline}`}
                >
                  {KARATS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Grams
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  required
                  value={h.grams}
                  onChange={(e) => patchRow(i, { grams: e.target.value })}
                  placeholder="12.5"
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
              <label className="w-[130px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Buy rate (₹/g)
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  value={h.buyPerGram}
                  onChange={(e) => patchRow(i, { buyPerGram: e.target.value })}
                  placeholder="9100"
                  className={`num w-full text-[15px] ${underline}`}
                />
              </label>
            </div>

            {rowValue && (
              <div className="num pt-2 text-[12px] text-vaulzo-fainter">
                ≈ {formatCurrency(rowValue)} at ₹{Math.round(rate).toLocaleString("en-IN")}/g
                {rowGain != null && (
                  <span className={rowGain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                    {" "}
                    · {rowGain >= 0 ? "↑" : "↓"} {Math.abs(rowGain).toFixed(2)}%
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add item
      </button>

      {total > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total <span className="font-semibold text-vaulzo-ink">{formatCurrency(total)}</span>
          {gain != null && (
            <span className={`font-semibold ${gain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}`}>
              {" "}
              · {gain >= 0 ? "↑" : "↓"} {Math.abs(gain).toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
